import { StepProgressItem, StepStatus } from '@/components/pages/dashboard/dashboard.types';

// Merge reward + punishment into a single "Reward & Punishment" display step.
// The merged step is done only when both are done; otherwise not_started.
export const normalizeSteps = (steps: StepProgressItem[]): StepProgressItem[] => {
  const out: StepProgressItem[] = [];
  for (const step of steps) {
    if (step.step_key === 'punishment') {
      const rewardIdx = out.findIndex(s => s.step_key === 'reward');
      if (rewardIdx >= 0) {
        const bothDone = step.status === 'done' && out[rewardIdx].status === 'done';
        out[rewardIdx] = {
          ...out[rewardIdx],
          step_name: 'Reward & Punishment',
          status: bothDone ? 'done' : 'not_started',
        };
      }
      continue;
    }
    out.push(step);
  }
  return out;
};

// Marks the first not-done step as "in_progress", but only once some progress
// has already been made elsewhere — otherwise the whole flow is "not_started".
export const deriveStepStatuses = (steps: StepProgressItem[]): StepProgressItem[] => {
  const firstNotStarted = steps.findIndex(step => step.status !== 'done');
  if (firstNotStarted < 0) return steps;
  return steps.map((step, index) => {
    if (index === firstNotStarted) {
      const hasAnyProgress = steps.some(s => s.status === 'done' || s.count > 0);
      if (!hasAnyProgress) {
        return { ...step, status: 'not_started' };
      }
      return { ...step, status: 'in_progress' as StepStatus };
    }
    return step;
  });
};

// Convenience: normalize + derive in one call, and return a step_key -> status
// lookup for consumers (e.g. the sidebar) that only need per-step status.
export const buildStepStatusMap = (rawSteps: StepProgressItem[]): Map<string, StepStatus> => {
  const steps = deriveStepStatuses(normalizeSteps(rawSteps));
  const map = new Map<string, StepStatus>();
  for (const step of steps) {
    map.set(step.step_key, step.status);
    if (step.step_key === 'reward') {
      // The merged Reward & Punishment step represents both backend keys.
      map.set('punishment', step.status);
    }
  }
  return map;
};
